<script setup lang="ts">
import { onMounted, ref } from 'vue'
import ModalDialog from '../UI/Modal/ModalDialog.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import useApi from '../../Composables/useApi'
import useLoading from '../../../../Composables/useLoading.ts'
import useModal from '../../../../Composables/useModal.ts'
import type { AttachedEntity, UserEntityResponse, UserLookupUser } from '../../../../Types/user'
import InfoNotice from '../UI/Notice/InfoNotice.vue'
import { useI18n } from 'vue-i18n'

type FormCreatorType = InstanceType<typeof FormCreator>
type FormParams = {
  entityId: number
  contractId?: number
}
interface FormCreatorApi {
  message: { waitingTextT: string; successTextT: string }
  methods: (method: 'post' | 'patch', arg2?: unknown, arg3?: unknown, endpoint?: string) => void
}

type DefUserEntity = Record<string, unknown> & {
  user?: {
    userEntities?: Array<{ id: number; entityName?: string | null; positionName?: string | null }>
  }
}

const props = defineProps<{
  user: UserLookupUser
  entityId: number
  contractId?: number | null
}>()

const emit = defineEmits<{
  close: []
  save: [{ userEntity: UserEntityResponse }]
}>()

const { t } = useI18n()
const { getJSON } = useApi()
const { loading, setLoading } = useLoading(true)
const { loading: sending, setLoading: setSending } = useLoading(false)
const { modal, modalId, open, close } = useModal({ prefix: 'attach-user-' })

const formCreator = ref<FormCreatorType | null>(null)
const formData = ref<Record<string, unknown> | null>(null)
const def = ref<Record<string, unknown> | null>(null)
const userEntity = ref<DefUserEntity | null>(null)
const attachedEntities = ref<AttachedEntity[]>([])

const endpoint = `users/${props.user.id}/entities`
const params: FormParams = {
  entityId: props.entityId,
  ...(props.contractId ? { contractId: props.contractId } : {}),
}

function extractAttachedEntities(value: DefUserEntity | null): AttachedEntity[] {
  return (value?.user?.userEntities ?? [])
    .filter((existing) => !!existing.entityName)
    .map((existing) => ({
      id: existing.id,
      name: existing.entityName ?? null,
      position: existing.positionName ?? null,
    }))
}

async function loadDef() {
  setLoading(true)
  try {
    const query = new URLSearchParams({ entityId: String(params.entityId) })
    if (params.contractId) {
      query.set('contractId', String(params.contractId))
    }
    const json = await getJSON(`${endpoint}/def?${query.toString()}`)
    def.value = json.config
    userEntity.value = json.userEntity
    attachedEntities.value = extractAttachedEntities(json.userEntity)
  } catch (reason) {
    cancel()
  } finally {
    setLoading(false)
  }
}

function submit() {
  const form = formCreator.value
  if (!form || sending.value) {
    return
  }

  form.message = {
    waitingTextT: 'userLookup.attach.waitingText',
    successTextT: 'userLookup.attach.successText',
  }
  form.methods('post', null, null, endpoint)
}

function onUpdateData(newData: Record<string, unknown>) {
  formData.value = newData
}

function onObjectReturn({ object, context }: { object: UserEntityResponse | null; context: string }) {
  if (object && context === 'post') {
    close()
    emit('save', { userEntity: object })
  }
}

function cancel() {
  close()
  emit('close')
}

onMounted(async () => {
  open()
  await loadDef()
})
</script>

<template>
  <ModalDialog
    :id="modalId"
    ref="modal"
    :title="t('userLookup.attach.title')"
    class-modal-dialog="modal-lg"
    class-body="px-3"
    e2e-modal="modal-attach-user"
    @icon-close="emit('close')"
  >
    <template #body>
      <FormCreator
        v-if="def && userEntity"
        ref="formCreator"
        endpoint="users"
        dynamic
        send-params
        src-error="formattachuser"
        class-to-add="d-none"
        :params="params"
        :config-def="def"
        :config-object="userEntity"
        @update-data="onUpdateData"
        @object-return="onObjectReturn"
        @is-sending="setSending"
      />

      <LazyContent :loading="loading" :class="{ 'p-2': loading }">
        <div v-if="formData" class="row">
          <div class="col-6">
            <FieldCreator
              name="user.lastname"
              form-ref="formattachuser"
              :form-data="formData"
              domain="userEntity"
              label-top
            />
          </div>
          <div class="col-6">
            <FieldCreator
              name="user.firstname"
              form-ref="formattachuser"
              :form-data="formData"
              domain="userEntity"
              label-top
            />
          </div>
          <div class="col-6">
            <FieldCreator
              name="user.phone"
              form-ref="formattachuser"
              :form-data="formData"
              domain="userEntity"
              label-top
            />
          </div>
          <div class="col-6">
            <FieldCreator
              name="user.email"
              form-ref="formattachuser"
              :form-data="formData"
              domain="userEntity"
              label-top
            />
          </div>
          <div class="col-12">
            <FieldCreator
              name="position"
              form-ref="formattachuser"
              :form-data="formData"
              domain="userEntity"
              label-top
            />
          </div>
          <div v-if="attachedEntities.length" class="col-12 mb-3">
            <InfoNotice
              icon="fas fa-users"
              :title="t('userLookup.attach.attachedEntities.title')"
              :text="t('userLookup.attach.attachedEntities.text')"
              :items="attachedEntities"
              :item-key="(entity) => entity.id"
              e2e-data="attachedEntitiesNotice"
            >
              <template #item="{ item }">
                {{ item.name ?? '—' }}
                <span v-if="item.position" class="font-weight-normal">· {{ item.position }}</span>
              </template>
            </InfoNotice>
          </div>
        </div>
      </LazyContent>
    </template>
    <template v-if="!loading" #footer>
      <div class="d-flex w-100 justify-content-end">
        <ButtonAll margin="no" type="default" label="button.cancel" name="attachUserClose" @click="cancel" />
        <ButtonAll
          margin="no"
          type="action"
          label="button.confirm"
          class="ml-2"
          name="attachUserValidate"
          :loading="sending"
          @click="submit"
        />
      </div>
    </template>
  </ModalDialog>
</template>
